import { call, put, takeLatest, takeEvery } from 'redux-saga/effects';
import callApi, { abortableCallApi } from 'utils/callApi';
import { LOCATION_CHANGE } from 'react-router-redux';

import { fetchAccountInformationSuccess } from 'redux/modules/account';
import { fetchAppInfoSuccess } from 'redux/modules/appInfo';
import { addNotification } from 'redux/modules/notifications';

// Actions
export const FETCH_DOCUMENT_CATEGORIES = 'FETCH_DOCUMENT_CATEGORIES';
export const FETCH_DOCUMENT_CATEGORIES_SUCCESS = 'FETCH_DOCUMENT_CATEGORIES_SUCCESS';
export const FETCH_DOCUMENT_CATEGORIES_FAIL = 'FETCH_DOCUMENT_CATEGORIES_FAIL';
export const CLEAR_DOCUMENT_CATEGORIES = 'CLEAR_DOCUMENT_CATEGORIES';
export const CREATE_DOCUMENT_CATEGORY = 'CREATE_DOCUMENT_CATEGORY';
export const CREATE_DOCUMENT_CATEGORY_SUCCESS = 'CREATE_DOCUMENT_CATEGORY_SUCCESS';
export const CREATE_DOCUMENT_CATEGORY_FAIL = 'CREATE_DOCUMENT_CATEGORY_FAIL';
export const UPDATE_DOCUMENT_CATEGORY = 'UPDATE_DOCUMENT_CATEGORY';
export const UPDATE_DOCUMENT_CATEGORY_SUCCESS = 'UPDATE_DOCUMENT_CATEGORY_SUCCESS';
export const UPDATE_DOCUMENT_CATEGORY_FAIL = 'UPDATE_DOCUMENT_CATEGORY_FAIL';
export const DELETE_DOCUMENT_CATEGORY = 'DELETE_DOCUMENT_CATEGORY';
export const DELETE_DOCUMENT_CATEGORY_SUCCESS = 'DELETE_DOCUMENT_CATEGORY_SUCCESS';
export const DELETE_DOCUMENT_CATEGORY_FAIL = 'DELETE_DOCUMENT_CATEGORY_FAIL';

// Reducer
export default function documentCategoriesReducer(state = {
  data: [], count: 0, loading: 0,
}, action) {
  const { type } = action;
  let triggerLoading;
  let payload;
  if (action.payload) {
    ({ triggerLoading, ...payload } = action.payload);
  }
  switch (type) {
    case FETCH_DOCUMENT_CATEGORIES:
      return {
        ...state,
        ...triggerLoading && state.loading <= 0 ? { loading: state.loading + 1 } : {},
      };
    case FETCH_DOCUMENT_CATEGORIES_SUCCESS: {
      if (payload.push) {
        const newData = [
          ...state.data.slice(0, state.data.length),
          ...payload.data.slice(0, payload.data.length),
        ];
        return {
          ...state,
          count: payload.count,
          data: newData,
          ...triggerLoading ? { loading: state.loading - 1 } : {},
        };
      }
      return {
        ...state,
        ...payload,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    }
    case FETCH_DOCUMENT_CATEGORIES_FAIL:
      return {
        ...state,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    case CLEAR_DOCUMENT_CATEGORIES:
      return {
        ...state,
        data: [],
        count: 0,
      };
    case CREATE_DOCUMENT_CATEGORY_SUCCESS: {
      const newData = [
        ...state.data.slice(0, state.data.length),
        payload,
      ];
      return {
        ...state,
        count: state.count + 1,
        data: newData,
      };
    }
    case UPDATE_DOCUMENT_CATEGORY_SUCCESS: {
      const newData = state.data.map((dataItem) => {
        if (dataItem.ID !== payload.ID) {
          return dataItem;
        }
        return {
          ...dataItem,
          ...payload,
        };
      });
      return {
        ...state,
        data: newData,
      };
    }
    case DELETE_DOCUMENT_CATEGORY_SUCCESS: {
      const index = state.data.findIndex(dataItem => dataItem.ID === payload.ID);
      const newData = [
        ...state.data.slice(0, index),
        ...state.data.slice(index + 1),
      ];
      return {
        ...state,
        count: state.count - 1,
        data: newData,
      };
    }
    case LOCATION_CHANGE:
      return {
        ...state,
        loading: 0,
      };
    default:
      return state;
  }
}

// Action Creators
export function fetchDocumentCategories(payload) {
  return { type: FETCH_DOCUMENT_CATEGORIES, payload };
}

export function fetchDocumentCategoriesSuccess(payload) {
  return { type: FETCH_DOCUMENT_CATEGORIES_SUCCESS, payload };
}

export function fetchDocumentCategoriesFail(payload) {
  return { type: FETCH_DOCUMENT_CATEGORIES_FAIL, payload };
}

export function clearDocumentCategories(payload) {
  return { type: CLEAR_DOCUMENT_CATEGORIES, payload };
}

export function createDocumentCategory(payload) {
  return { type: CREATE_DOCUMENT_CATEGORY, payload };
}

export function createDocumentCategorySuccess(payload) {
  return { type: CREATE_DOCUMENT_CATEGORY_SUCCESS, payload };
}

export function createDocumentCategoryFail(payload) {
  return { type: CREATE_DOCUMENT_CATEGORY_FAIL, payload };
}

export function updateDocumentCategory(payload) {
  return { type: UPDATE_DOCUMENT_CATEGORY, payload };
}

export function updateDocumentCategorySuccess(payload) {
  return { type: UPDATE_DOCUMENT_CATEGORY_SUCCESS, payload };
}

export function updateDocumentCategoryFail(payload) {
  return { type: UPDATE_DOCUMENT_CATEGORY_FAIL, payload };
}

export function deleteDocumentCategory(payload) {
  return { type: DELETE_DOCUMENT_CATEGORY, payload };
}

export function deleteDocumentCategorySuccess(payload) {
  return { type: DELETE_DOCUMENT_CATEGORY_SUCCESS, payload };
}

export function deleteDocumentCategoryFail(payload) {
  return { type: DELETE_DOCUMENT_CATEGORY_FAIL, payload };
}

// Requests

let abort;

export function requestFetchDocumentCategories(payload) {
  if (abort) {
    abort();
    abort = null;
  }
  const request = payload
    ? abortableCallApi(`resource/document_category/?limit=${payload.limit}&offset=${payload.offset}`)
    : abortableCallApi('resource/document_category/');
  abort = request.abort;
  return request.res;
}

export function requestSearchDocumentCategories({ limit, offset, filters }) {
  if (abort) {
    abort();
    abort = null;
  }
  const request = abortableCallApi('resource/document_category/search/', 'POST', {
    filters,
    sort_by: [
      {
        name: 'Name',
        sort: 'asc',
      },
      {
        name: 'ID',
        sort: 'asc',
      },
    ],
    limit,
    offset,
  });
  abort = request.abort;
  return request.res;
}

export function requestCreateDocumentCategory(payload) {
  return callApi('resource/document_category/', 'POST', payload);
}

export function requestUpdateDocumentCategory(payload) {
  return callApi('resource/document_category/', 'PATCH', payload);
}

export function requestDeleteDocumentCategory(payload) {
  return callApi(`resource/document_category/?id=${payload}`, 'DELETE');
}

// Sagas
function* fetchDocumentCategoriesSaga(action) {
  const { triggerLoading, filters } = action.payload;
  try {
    const res = filters
      ? yield call(requestSearchDocumentCategories, action.payload)
      : yield call(requestFetchDocumentCategories, action.payload);
    yield put(fetchDocumentCategoriesSuccess({
      data: res.data,
      count: res.count,
      triggerLoading,
      ...action.payload.push ? { push: true } : {},
    }));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
  } catch (err) {
    yield put(fetchDocumentCategoriesFail({
      ...err.data,
      triggerLoading,
    }));
  }
}

function* createDocumentCategorySaga(action) {
  const {
    values,
    resolve,
    reject,
  } = action.payload;
  try {
    const res = yield call(requestCreateDocumentCategory, values);
    yield put(createDocumentCategorySuccess(res.data));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    yield put(addNotification({
      message: 'New Document Category successfully added',
      variant: 'success',
    }));
    if (resolve) {
      resolve(res.data);
    }
  } catch (err) {
    yield put(createDocumentCategoryFail(err.data));
    if (reject) {
      reject(err.data);
    }
  }
}

function* updateDocumentCategorySaga(action) {
  const {
    values,
    resolve,
    reject,
  } = action.payload;
  try {
    const res = yield call(requestUpdateDocumentCategory, values);
    yield put(updateDocumentCategorySuccess(res.data));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    yield put(addNotification({
      message: 'Document Category successfully updated',
      variant: 'success',
    }));
    if (resolve) {
      resolve(res.data);
    }
  } catch (err) {
    yield put(updateDocumentCategoryFail(err.data));
    if (reject) {
      reject(err.data);
    }
  }
}

function* deleteDocumentCategorySaga(action) {
  const { ID } = action.payload;
  try {
    const res = yield call(requestDeleteDocumentCategory, ID);
    yield put(deleteDocumentCategorySuccess({ ID }));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    yield put(addNotification({
      message: 'Document Category successfully deleted',
      variant: 'success',
    }));
  } catch (err) {
    yield put(deleteDocumentCategoryFail(err.data));
    yield put(addNotification({
      message: 'Failed to delete Document Category',
      variant: 'warning',
    }));
  }
}

export function* documentCategoriesSaga() {
  yield takeLatest(FETCH_DOCUMENT_CATEGORIES, fetchDocumentCategoriesSaga);
  yield takeEvery(CREATE_DOCUMENT_CATEGORY, createDocumentCategorySaga);
  yield takeEvery(UPDATE_DOCUMENT_CATEGORY, updateDocumentCategorySaga);
  yield takeEvery(DELETE_DOCUMENT_CATEGORY, deleteDocumentCategorySaga);
}



// WEBPACK FOOTER //
// ./src/redux/modules/documentCategories.js